import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sun, CalendarDays } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full px-4 py-2.5 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#1E1E1E]/60 mb-2";
const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }); } catch { return iso; } };
const emptyOverride = { override_saturday_date: "", override_friday_time: "", override_saturday_time: "" };

export const SabbathSettings = () => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ enabled: true, message: "Happy Sabbath", ...emptyOverride });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/settings/sabbath").then((r) => {
    const { enabled, message, override_saturday_date, override_friday_time, override_saturday_time } = r.data;
    setState(r.data);
    setForm({ enabled, message, override_saturday_date: override_saturday_date || "", override_friday_time: override_friday_time || "", override_saturday_time: override_saturday_time || "" });
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try { await api.put("/settings/sabbath", payload); toast.success("Sabbath banner settings saved"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const overrideActive = state?.source?.startsWith("Admin override");

  return (
    <div data-testid="admin-settings-panel">
      <h2 className="font-display text-2xl font-bold text-[#003B5C] mb-6">Happy Sabbath Banner</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={(e) => { e.preventDefault(); save(form); }} className="bg-white border border-gray-100 rounded-sm p-6 space-y-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="sabbath-enabled-toggle" className="accent-[#003B5C] w-4 h-4" />
            <span className="text-sm font-medium text-[#1E1E1E]">Show the banner automatically each Sabbath</span>
          </label>
          <div>
            <label className={labelCls}>Message</label>
            <input type="text" value={form.message} maxLength={80} onChange={set("message")} data-testid="sabbath-message-input" className={inputCls} />
          </div>

          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center gap-2 text-sm font-bold text-[#003B5C] mb-1"><CalendarDays size={15} className="text-[#D4A437]" /> Sunset override for one week (optional)</div>
            <p className="text-xs text-[#1E1E1E]/55 mb-4">Use only if the calendar time needs correcting for a specific Sabbath. Pick any date in that week — the override applies to that Sabbath only and automatically expires afterwards.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Week / Sabbath date</label>
                <input type="date" value={form.override_saturday_date} onChange={set("override_saturday_date")} data-testid="sabbath-override-date" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Friday sunset</label>
                <input type="time" value={form.override_friday_time} onChange={set("override_friday_time")} data-testid="sabbath-override-friday" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Saturday sunset</label>
                <input type="time" value={form.override_saturday_time} onChange={set("override_saturday_time")} data-testid="sabbath-override-saturday" className={inputCls} />
              </div>
            </div>
            {form.override_saturday_date && (
              <button type="button" onClick={() => save({ ...form, ...emptyOverride })} data-testid="sabbath-override-clear" className="mt-3 text-xs text-red-600 underline">Clear override</button>
            )}
          </div>

          <button type="submit" disabled={saving} data-testid="sabbath-save-button" className="bg-[#003B5C] text-white px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-[#002A42] disabled:opacity-60">{saving ? "Saving…" : "Save Settings"}</button>
        </form>

        <div className="bg-white border border-gray-100 rounded-sm p-6 text-sm text-[#1E1E1E]/80 space-y-3">
          <div className="flex items-center gap-2 font-bold text-[#003B5C]"><Sun size={16} className="text-[#D4A437]" /> This week's Sabbath — Garland, Texas</div>
          {state ? (
            <>
              <div>Status now: <span data-testid="sabbath-status" className={`font-semibold ${state.active ? "text-green-700" : "text-[#1E1E1E]/60"}`}>{state.active ? "Banner is showing" : "Banner hidden"}</span></div>
              <div data-testid="sabbath-begins">Sabbath begins: <strong>{fmtDate(state.friday_date)} · {state.friday_sunset}</strong></div>
              <div data-testid="sabbath-ends">Sabbath ends: <strong>{fmtDate(state.saturday_date)} · {state.saturday_sunset}</strong></div>
              <div className={`text-xs rounded-sm px-3 py-2 ${overrideActive ? "bg-amber-50 text-amber-900 border border-amber-200" : "bg-[#F7F8FA] text-[#1E1E1E]/60"}`} data-testid="sabbath-source">
                Source: {state.source}
              </div>
              <p className="text-xs text-[#1E1E1E]/55">Times come from the Texas Conference 2026 Sunset Calendar (DFW/Keene column, the closest listed location to Garland). The listed Saturday time is used for Friday as well. Dates outside the calendar fall back to a calculated sunset for Garland.</p>
            </>
          ) : <div className="text-[#1E1E1E]/50">Loading…</div>}
        </div>
      </div>
    </div>
  );
};
